/**
 * マニュアルの部品: 手順・注意書き・画面のイメージ（ブラウザ枠・スマホ枠・押す場所の印）。
 * 外部サービスの画面は、要点だけを残した簡略図にする（ロゴや配色は真似しない）。
 */
import type { ReactNode } from 'react'
import { AlertTriangle, Info, Lightbulb, Lock } from 'lucide-react'

/** 番号つきの手順。左に番号、右に説明と画面のイメージ */
export function Step({ n, title, children, mock }: { n: number; title: string; children: ReactNode; mock?: ReactNode }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)] gap-4 border-t border-line py-6 first:border-t-0 first:pt-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-6">
      <div className="flex gap-3">
        <span aria-hidden className="gm-num flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">{n}</span>
        <div className="min-w-0 space-y-2 text-sm leading-relaxed">
          <h3 className="pt-1 text-base font-bold leading-snug"><span className="sr-only">手順 {n}: </span>{title}</h3>
          {children}
        </div>
      </div>
      {mock && <div className="min-w-0">{mock}</div>}
    </li>
  )
}

export function Steps({ children }: { children: ReactNode }) {
  return <ol className="gm-card list-none p-4 md:p-6">{children}</ol>
}

const CALLOUT = {
  tip: { icon: Lightbulb, cls: 'border-ok/30 bg-ok-bg text-ok', label: 'ヒント' },
  warn: { icon: AlertTriangle, cls: 'border-warn/30 bg-warn-bg text-warn', label: '注意' },
  info: { icon: Info, cls: 'border-primary/20 bg-primary-subtle text-primary', label: '補足' },
  secret: { icon: Lock, cls: 'border-bad/25 bg-bad-bg text-bad', label: '秘密の扱い' },
} as const

/** 注意・ヒントの囲み。色だけに頼らず、見出しの文字でも種類を示す */
export function Callout({ kind, title, children }: { kind: keyof typeof CALLOUT; title?: string; children: ReactNode }) {
  const c = CALLOUT[kind]
  const Icon = c.icon
  return (
    <div className={`flex gap-2.5 rounded-ctl border px-3 py-2.5 text-sm ${c.cls}`}>
      <Icon size={16} aria-hidden className="mt-0.5 shrink-0" />
      <div className="min-w-0 space-y-1 text-ink">
        <p className={`font-bold ${c.cls.split(' ').find(x => x.startsWith('text-'))}`}>{title ?? c.label}</p>
        <div className="leading-relaxed">{children}</div>
      </div>
    </div>
  )
}

/** 画面上の文言（ボタン名・メニュー名）。英語表示のときの名前も添えられる */
export function UiLabel({ children, en }: { children: ReactNode; en?: string }) {
  return (
    <span className="box-decoration-clone rounded border border-line bg-surface-2 px-1.5 py-px text-[13px] font-bold text-ink [overflow-wrap:anywhere]">
      {children}{en && <span className="font-normal text-ink-muted">（{en}）</span>}
    </span>
  )
}

export function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-surface-2 px-1 py-px font-mono text-[12.5px] text-ink">{children}</code>
}

// ── 画面のイメージ ────────────────────────────────────────

/** ブラウザ枠（アドレス欄つき）。中身は簡略図 */
export function BrowserFrame({ url, children, caption }: { url: string; children: ReactNode; caption?: string }) {
  return (
    <figure className="overflow-hidden rounded-card border border-line-strong bg-surface shadow-sm">
      <div aria-hidden className="flex items-center gap-2 border-b border-line bg-surface-2 px-3 py-2">
        <span className="flex gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
          <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
          <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
        </span>
        <span className="min-w-0 flex-1 truncate rounded-full border border-line bg-surface px-3 py-0.5 font-mono text-[11px] text-ink-muted">{url}</span>
      </div>
      <div className="bg-surface p-3 text-[12.5px] leading-snug">{children}</div>
      <MockCaption text={caption} />
    </figure>
  )
}

/** スマホ枠。アプリでの操作（招待の承認・プロアカウントへの切り替え）に使う */
export function PhoneFrame({ title, children, caption }: { title: string; children: ReactNode; caption?: string }) {
  return (
    <figure className="mx-auto w-full max-w-[300px]">
      <div className="overflow-hidden rounded-[28px] border-[6px] border-ink/85 bg-surface shadow-sm">
        <div aria-hidden className="flex justify-center bg-surface pt-1.5"><span className="h-1.5 w-16 rounded-full bg-ink/80" /></div>
        <div className="border-b border-line px-3 py-2 text-center text-[13px] font-bold">{title}</div>
        <div className="space-y-1 p-3 text-[12.5px] leading-snug">{children}</div>
      </div>
      <MockCaption text={caption} />
    </figure>
  )
}

function MockCaption({ text }: { text?: string }) {
  return (
    <figcaption className="border-t border-line bg-surface px-3 py-1.5 text-[11px] text-ink-muted">
      {text ?? '画面はイメージです。実際の表示や名前は変わることがあります。'}
    </figcaption>
  )
}

/** 押す場所・見る場所の印（ピンクの枠と番号）。番号は本文の手順と対応させる */
export function Mark({ n, children, block }: { n?: number | string; children: ReactNode; block?: boolean }) {
  const Tag = block ? 'div' : 'span'
  return (
    <Tag className={`relative ${block ? 'block' : 'inline-block'} rounded-[7px] outline outline-2 outline-offset-2 outline-accent`}>
      {children}
      {n !== undefined && (
        <span aria-hidden className="gm-num absolute -right-2.5 -top-2.5 z-10 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-bold text-white shadow">
          {n}
        </span>
      )}
    </Tag>
  )
}

/** 簡略図のボタン */
export function MockButton({ children, kind = 'secondary' }: { children: ReactNode; kind?: 'primary' | 'secondary' | 'plain' }) {
  const cls = kind === 'primary' ? 'border-ink bg-ink text-surface'
    : kind === 'plain' ? 'border-transparent text-primary underline'
      : 'border-line-strong bg-surface text-ink'
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-[12px] font-bold ${cls}`}>{children}</span>
}

/** 簡略図の入力欄 */
export function MockInput({ label, value, secret }: { label: string; value?: string; secret?: boolean }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] text-ink-muted">{label}</div>
      <div className="truncate rounded-md border border-line-strong bg-surface px-2 py-1 font-mono text-[12px]">
        {value ? (secret ? '•'.repeat(Math.min(value.length, 22)) : value) : <span className="text-ink-muted/60">　</span>}
      </div>
    </div>
  )
}

/** 簡略図の左メニュー＋本文 */
export function MockSidebarLayout({ menu, active, children }: { menu: string[]; active?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 sm:grid-cols-[120px_minmax(0,1fr)]">
      <ul className="space-y-0.5 border-r border-line pr-2 text-[11.5px]">
        {menu.map(m => (
          <li key={m} className={`truncate rounded px-1.5 py-1 ${m === active ? 'bg-surface-2 font-bold text-ink' : 'text-ink-muted'}`}>{m}</li>
        ))}
      </ul>
      <div className="min-w-0 space-y-2">{children}</div>
    </div>
  )
}

/** 簡略図の行（一覧の1行） */
export function MockRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 rounded-md border border-line px-2 py-1.5">{children}</div>
}

/** Genemo Post 自身の画面のイメージ（ペルソナ画面など）。外部サービスと区別するため、アプリの見た目で描く */
export function PostScreen({ title, children, caption }: { title: string; children: ReactNode; caption?: string }) {
  return (
    <BrowserFrame url={`post.genemo.jp${title}`} caption={caption ?? 'Genemo Post の画面です。'}>
      {children}
    </BrowserFrame>
  )
}

/** 準備するものの一覧 */
export function Checklist({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-1.5 text-sm">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span aria-hidden className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded border border-primary text-[10px] text-primary">✓</span>
          <span className="leading-relaxed">{it}</span>
        </li>
      ))}
    </ul>
  )
}

/** うまくいかないとき（表示される文 → 原因と直し方） */
export function Trouble({ items }: { items: Array<{ symptom: ReactNode; fix: ReactNode }> }) {
  return (
    <dl className="divide-y divide-line overflow-hidden rounded-card border border-white bg-card text-sm shadow-card">
      {items.map((it, i) => (
        <div key={i} className="grid gap-1 px-4 py-3 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-4">
          <dt className="font-bold leading-relaxed">{it.symptom}</dt>
          <dd className="leading-relaxed text-ink-muted">{it.fix}</dd>
        </div>
      ))}
    </dl>
  )
}

export function SectionTitle({ children, id }: { children: ReactNode; id?: string }) {
  return <h2 id={id} className="pt-2 text-lg font-bold leading-snug">{children}</h2>
}
